import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, History, RotateCcw, ShieldCheck, Plus, RefreshCw, Download, Upload } from "lucide-react";
import { listBackups, restoreBackup, createBackup, exportContent, exportBackup, importContent, getBackupSettings, putBackupSettings } from "@/lib/api";

const REASONS = {
  publish: { label: "Avant publication", cls: "text-electric border-electric/40" },
  reset: { label: "Avant réinitialisation", cls: "text-red-400 border-red-400/40" },
  manual: { label: "Manuelle", cls: "text-emerald-400 border-emerald-400/40" },
  import: { label: "Avant import", cls: "text-amber-400 border-amber-400/40" },
  auto: { label: "Automatique", cls: "text-sky-400 border-sky-400/40" },
  weekly: { label: "Hebdomadaire (auto)", cls: "text-sky-400 border-sky-400/40" },
};

const FREQ_OPTIONS = [
  { value: "daily", label: "Quotidienne" },
  { value: "weekly", label: "Hebdomadaire" },
  { value: "monthly", label: "Mensuelle" },
  { value: "off", label: "Désactivée" },
];

const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
const downloadJson = (obj, filename) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
};

// Full backup history + restore + manual snapshot, embedded in the dashboard.
export const AdminBackups = ({ onRestored }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [freq, setFreq] = useState("weekly");
  const fileRef = useRef(null);

  const load = async () => {
    try { setItems(await listBackups()); }
    catch { toast.error("Impossible de charger les sauvegardes."); setItems([]); }
  };
  useEffect(() => { load(); getBackupSettings().then((r) => setFreq(r.frequency || "weekly")).catch(() => {}); }, []);

  const changeFreq = async (v) => {
    const prev = freq;
    setFreq(v);
    try { await putBackupSettings(v); toast.success("Fréquence de sauvegarde mise à jour."); }
    catch { setFreq(prev); toast.error("Échec de la mise à jour de la fréquence."); }
  };

  const createNow = async () => {
    setBusy(true);
    try { await createBackup(label.trim()); setLabel(""); toast.success("Sauvegarde créée."); await load(); }
    catch { toast.error("Échec de la création de la sauvegarde."); }
    finally { setBusy(false); }
  };

  const exportSite = async () => {
    setBusy(true);
    try { const res = await exportContent(); downloadJson(res, `karams-site-${stamp()}.json`); toast.success("Export du site téléchargé."); }
    catch { toast.error("Échec de l'export."); }
    finally { setBusy(false); }
  };

  const exportOne = async (bk) => {
    setBusy(true);
    try { const res = await exportBackup(bk.id); downloadJson(res, `karams-sauvegarde-${stamp()}.json`); toast.success("Sauvegarde téléchargée."); }
    catch { toast.error("Échec de l'export."); }
    finally { setBusy(false); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!f) return;
    let parsed;
    try { parsed = JSON.parse(await f.text()); }
    catch { toast.error("Fichier .json invalide ou illisible."); return; }
    const data = parsed && typeof parsed === "object" && parsed.data ? parsed.data : parsed;
    if (!data || typeof data !== "object") { toast.error("Ce fichier ne contient pas de données de site valides."); return; }
    if (!window.confirm(`Importer « ${f.name} » ?\n\n⚠️ Le contenu actuellement EN LIGNE sera REMPLACÉ et publié. Une sauvegarde automatique du contenu actuel est conservée (« Avant import »).\n\nContinuer ?`)) return;
    setBusy(true);
    try {
      const res = await importContent(data);
      onRestored?.(res.data);
      toast.success("Sauvegarde importée et publiée.");
      await load();
    } catch { toast.error("Échec de l'import. Vérifiez le fichier."); }
    finally { setBusy(false); }
  };

  const restore = async (bk) => {
    if (!window.confirm(`Restaurer la sauvegarde du ${new Date(bk.created_at).toLocaleString("fr-FR")} ?\n\nLe contenu actuellement en ligne sera remplacé et publié. Une sauvegarde du contenu actuel est conservée.`)) return;
    setBusy(true);
    try {
      const res = await restoreBackup(bk.id);
      onRestored?.(res.data);
      toast.success("Sauvegarde restaurée et publiée.");
      await load();
    } catch { toast.error("Échec de la restauration."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-backups-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-1.5">
            <History size={16} className="text-electric" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Historique & restauration</span>
          </div>
          <h2 className="font-serif text-2xl text-white">Sauvegardes du contenu</h2>
          <p className="text-sm text-secondary2 mt-1 max-w-xl">Une sauvegarde est créée automatiquement avant chaque publication, chaque réinitialisation et chaque import, ainsi qu'<strong className="text-white">automatiquement une fois par semaine</strong>. Vous pouvez aussi en créer, en exporter ou en importer manuellement. Les 40 dernières sont conservées.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onImportFile} className="hidden" data-testid="import-file-input" />
          <button onClick={exportSite} disabled={busy} data-testid="export-site-btn" className="inline-flex items-center gap-2 text-xs px-3.5 py-1.5 rounded-full border border-white/15 text-white hover:border-electric hover:text-electric transition-colors disabled:opacity-50">
            <Download size={14} /> Exporter le site (.json)
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-btn" className="inline-flex items-center gap-2 text-xs px-3.5 py-1.5 rounded-full border border-white/15 text-white hover:border-electric hover:text-electric transition-colors disabled:opacity-50">
            <Upload size={14} /> Importer (.json)
          </button>
          <button onClick={load} disabled={busy} data-testid="backups-refresh-btn" className="inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric transition-colors disabled:opacity-50">
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between" data-testid="backup-frequency-box">
        <div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 block mb-0.5">Sauvegardes automatiques</span>
          <span className="text-sm text-white/80">Fréquence de la sauvegarde automatique du site</span>
        </div>
        <select value={freq} onChange={(e) => changeFreq(e.target.value)} data-testid="backup-frequency-select"
          className="bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric outline-none">
          {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="block text-[10px] tracking-[0.2em] uppercase text-secondary2 mb-1.5">Nom de la sauvegarde (optionnel)</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : Avant refonte du festival"
            data-testid="backup-label-input"
            className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-electric outline-none" />
        </div>
        <button onClick={createNow} disabled={busy} data-testid="create-backup-btn"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-electric text-black text-xs tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60 shrink-0">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Sauvegarder maintenant
        </button>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-16 text-secondary2"><Loader2 size={18} className="animate-spin mr-2" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-10 text-center text-secondary2 text-sm">
          <ShieldCheck size={28} className="mx-auto mb-3 text-white/20" />
          Aucune sauvegarde pour l'instant. Elles apparaîtront ici automatiquement après votre première publication.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="backups-list">
          {items.map((bk, i) => {
            const r = REASONS[bk.reason] || { label: bk.label || "Sauvegarde", cls: "text-white/60 border-white/20" };
            return (
              <li key={bk.id} data-testid={`backup-row-${i}`}
                className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] px-4 py-3 hover:border-white/20 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-2.5 py-0.5 ${r.cls}`}>{bk.label || r.label}</span>
                    <span className="text-sm text-white/90">{new Date(bk.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>
                  </div>
                  {bk.by && <span className="text-[11px] text-secondary2">Par {bk.by}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => exportOne(bk)} disabled={busy} data-testid={`export-backup-${i}`} title="Télécharger cette sauvegarde en .json"
                    className="inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric border border-white/15 hover:border-electric rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-50">
                    <Download size={13} /> Exporter
                  </button>
                  <button onClick={() => restore(bk)} disabled={busy} data-testid={`restore-backup-${i}`}
                    className="inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric border border-white/15 hover:border-electric rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-50">
                    <RotateCcw size={13} /> Restaurer
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
